import { UseFormReturn, useWatch } from 'react-hook-form';
import { IDocumentText } from '@models/Forms/IForms';
import { handlerFieldWatch } from '@utils/fieldWatchUtils';
import './Text.scss';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import React, { useEffect, useMemo, useState } from 'react';
import FieldWrapper from '@molecules/formbuilder/controls/FieldWrapper/FieldWrapper';

export interface ITextProps {
    text?: IDocumentText;
    formMethods: UseFormReturn<any>;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
}

const Text = ({ text, formMethods, isEdit, isNew, fields, ...props }: ITextProps) => {
    const displayFormat = text?.value;
    const displayFormatMng = useMemo(() => {
        const manager = new FormulaManager(displayFormat!);
        manager.Init(fields, formMethods);
        return manager;
    }, [displayFormat, fields, formMethods]);

    const visibilityRules = text?.visibilityRules;
    const visibilityMng = useMemo(() => {
        const manager = new FormulaManager(visibilityRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [visibilityRules, fields, formMethods]);

    const activated = React.useRef(false);
    const [visibility, setVisibility] = useState<boolean>(false);
    const watchDataDisplayFormat = React.useRef<any[]>([]);
    const [displayVal, setDisplayVal] = useState<string>('');

    const InitFormulas = async () => {
        let result = await visibilityMng.EvalFormulaValues(isEdit, isNew);
        if (activated.current) {
            setVisibility(result);
        }
        let values = displayFormatMng.GetDefaultValues(isEdit, isNew, true);
        let coll: any[] = [];
        values.forEach((x) => {
            if (x === null) {
                coll.push('');
            } else {
                coll.push(x);
            }
        });
        if (activated.current) {
            if (coll.length === 0 && text?.value) {
                setDisplayVal(text?.value);
            } else {
                let dataDisplay = await displayFormatMng.ReplaceFormulaValues(isEdit, isNew, coll, false);
                if (dataDisplay) {
                    dataDisplay = dataDisplay?.replace(/\'/g, '');
                    dataDisplay = dataDisplay?.replace(/\\n/g, '\n');
                    setDisplayVal(dataDisplay);
                }
            }
        }
    };

    useEffect(() => {
        activated.current = true;
        InitFormulas();
        return () => {
            activated.current = false;
        };
    }, []);

    const handlerDisplayVal = async (watch: any[]) => {
        let coll: any[] = [];
        watch.forEach((x) => {
            if (x === null) {
                coll.push('');
            } else {
                coll.push(x);
            }
        });

        if (activated.current) {
            if (coll.length === 0 && text?.value) {
                setDisplayVal(text?.value);
            } else {
                let dataDisplay = await displayFormatMng.ReplaceFormulaValues(isEdit, isNew, coll, false);
                if (dataDisplay) {
                    dataDisplay = dataDisplay?.replace(/\'/g, '');
                    dataDisplay = dataDisplay?.replace(/\\n/g, '\n');
                    setDisplayVal(dataDisplay);
                }
            }
        }
    };

    const watchDisplayFormat = useWatch({
        name: displayFormatMng.GetWatchFields(),
    });

    useEffect(() => {
        watchDataDisplayFormat.current = watchDisplayFormat;
        handlerDisplayVal(watchDisplayFormat);
    }, [watchDisplayFormat]);

    const watchVisibility = useWatch({
        name: visibilityMng.GetWatchFields(),
    });

    useEffect(() => {
        handlerFieldWatch(watchVisibility, isEdit, isNew, visibilityMng, setVisibility, activated);
    }, [watchVisibility, isEdit, isNew]);

    const style: React.CSSProperties = (() => {
        try {
            return text?.style ? JSON.parse(text.style.replace(/'/g, '"')) : {};
        } catch {
            return {};
        }
    })();

    return text && text.value && visibility ? (
        <div className="form-field" data-testid={text.id ? `text-${text.id}` : undefined}>
            <FieldWrapper inLineFormat={false} label={text.name} title={text.viewTitle} testid={text.id}>
                <div
                    className="content-text"
                    style={style}
                    dangerouslySetInnerHTML={{ __html: displayVal?.toString() }}
                />
            </FieldWrapper>
        </div>
    ) : (
        <></>
    );
};

export default Text;
